"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { getUnreadNotificationCount } from "@/lib/actions/notification-actions";
import { useAuth } from "@/providers/auth-provider";

/**
 * In-flight request dedupe, keyed by user id — same pattern as
 * use-coin-balance. On mobile editor pages the hidden desktop bell and the
 * mobile hamburger menu both want the unread count; sharing one call avoids a
 * duplicate query. Cleared once settled so a later mount still fetches fresh.
 */
const inflight = new Map<string, Promise<number>>();
export function fetchUnreadCount(userId: string): Promise<number> {
  let promise = inflight.get(userId);
  if (!promise) {
    promise = getUnreadNotificationCount().finally(() => {
      inflight.delete(userId);
    });
    inflight.set(userId, promise);
  }
  return promise;
}

/**
 * Returns the authenticated user's unread notification count, or `null` while
 * loading / signed out. Fetch-on-mount (matches the app's existing freshness
 * model — no polling yet).
 */
export function useUnreadNotificationCount() {
  const $ = _c(3);
  const {
    user
  } = useAuth();
  const [count, setCount] = useState(null);
  let t0;
  let t1;
  if ($[0] !== user) {
    t0 = () => {
      if (!user) {
        return;
      }
      let active = true;
      fetchUnreadCount(user.id).then(c => {
        if (active) {
          setCount(c);
        }
      }).catch(() => {
        if (active) {
          setCount(null);
        }
      });
      return () => {
        active = false;
      };
    };
    t1 = [user];
    $[0] = user;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return count;
}